package no.vareflow.web

import no.vareflow.app.core.VareflowCore
import no.vareflow.app.data.local.BrowserKeyValueStore
import no.vareflow.app.data.local.CountOutboxCodec
import no.vareflow.app.data.local.StorageKeys
import no.vareflow.app.domain.*
import no.vareflow.app.sync.*

/** Sync state shown in the header pill and in Mer → Synkronisering. */
enum class CloudState(private val nb: String, private val en: String, private val pillNb: String, private val pillEn: String) {
    SIGNED_OUT("Ikke logget inn", "Not signed in", "SKY", "CLOUD"),
    CONNECTING("Kobler til …", "Connecting …", "KOBLER", "CONNECTING"),
    ONLINE("Synkronisert", "Synced", "SKY OK", "CLOUD OK"),
    OFFLINE("Frakoblet – endringer sendes når nettet er tilbake", "Offline – changes are sent when the network is back", "OFFLINE", "OFFLINE"),
    ERROR("Feil ved synkronisering", "Sync error", "SKY FEIL", "CLOUD ERROR");

    val label: String get() = t(nb, en)
    val pill: String get() = t(pillNb, pillEn)
}

/** The signed-in account has no membership in the company that was asked for. */
class NoMembership(message: String) : IllegalStateException(message)

/** The signed-in person's membership in one company (same fields as the phone's CloudMembership). */
data class WebMembership(
    val uid: String,
    val companyId: String,
    val companyName: String,
    val companyShortName: String,
    val companyCode: String,
    val locationId: String,
    val locationName: String,
    val displayName: String,
    val email: String,
    val role: String,
    val permissions: Set<String>,
    val active: Boolean,
    val mustChangePassword: Boolean,
    /** Time-limited helper: last moment (ms) the person may work; null = no limit. */
    val accessUntil: Long? = null
) {
    fun toUserAccount(): UserAccount {
        val parsedRole = runCatching { Role.valueOf(role.uppercase()) }.getOrDefault(Role.EMPLOYEE)
        val parsedPermissions = if ("*" in permissions || parsedRole == Role.ADMIN) defaultPermissions(Role.ADMIN)
        else permissions.mapNotNull { raw -> runCatching { Permission.valueOf(raw) }.getOrNull() }.toSet().ifEmpty { defaultPermissions(parsedRole) }
        return UserAccount(
            id = uid, name = displayName, username = email, password = "", role = parsedRole,
            allowedCategories = emptySet(), permissions = parsedPermissions, accessUntil = accessUntil, active = active
        )
    }
}

data class CompanyChoice(val companyId: String, val name: String, val role: String)
data class CompanyCodeInfo(val code: String, val companyId: String, val name: String)

/** A line on the Eier page: the company, when it was made, and the owner's own role in it (null: not a member). */
data class OwnerCompanyRow(
    val company: CompanyProfile,
    val createdAt: Long?,
    val myRole: String?,
    val suspendedReason: String = "",
    val suspendedNote: String = ""
)

/** E-mail / Google sign-in opens the only company, or asks which one. */
sealed interface WebSignIn {
    data class Ready(val membership: WebMembership) : WebSignIn
    data class Choose(val choices: List<CompanyChoice>) : WebSignIn
}

/** The person closed the Google window: no error message. */
object LoginCancelled : Exception("Cancelled")

private object WebPaths {
    fun company(c: String) = "companies/$c"
    fun memberships(c: String) = "companies/$c/memberships"
    fun products(c: String) = "companies/$c/products"
    fun sessions(c: String, l: String) = "companies/$c/locations/$l/inventorySessions"
    fun assignments(c: String, l: String, s: String) = "${sessions(c, l)}/$s/categoryAssignments"
    fun countEntries(c: String, l: String, s: String) = "${sessions(c, l)}/$s/countEntries"
    fun conflicts(c: String, l: String, s: String) = "${sessions(c, l)}/$s/conflicts"
    fun changeEvents(c: String) = "companies/$c/changeEvents"
    fun companyCode(code: String) = "companyCodes/$code"
    fun logo(c: String) = "companies/$c/branding/logo"
    fun history(c: String, l: String) = "companies/$c/locations/$l/history"
    fun spotChecks(c: String, l: String, s: String) = "${sessions(c, l)}/$s/spotChecks"
    fun categories(c: String) = "companies/$c/categories"
    fun svinn(c: String, l: String) = "companies/$c/locations/$l/svinn"
}

private sealed interface WriteOutcome {
    data class Ack(val version: Int) : WriteOutcome
    data class Conflict(val remoteVersion: Int, val remoteQuantity: Double?, val remoteNote: String, val remoteActorId: String) : WriteOutcome
}

/**
 * Firebase for the web: sign-in (e-mail, Firmakode, Google), the company picker, realtime
 * listeners feeding a cloud-mode [VareflowCore], and the writes the core hands over as
 * [VareflowCore.CloudSink] – counts through a durable outbox and a transaction with the same
 * conflict rules as the phone (CountOutbox.decide).
 */
class WebCloud(
    private val onChange: () -> Unit,
    /** The cloud ended the session by itself (e.g. the person was deactivated): back to the login page. */
    private val onSignedOut: (message: String) -> Unit = {}
) : VareflowCore.CloudSink {
    private val sdk: dynamic = firebaseSdk()
    /** False when the Firebase SDK could not be loaded (then only local test mode works). */
    val available: Boolean get() = sdk != null

    private var app: dynamic = null
    private var auth: dynamic = null
    private var db: dynamic = null
    private val prefs = BrowserKeyValueStore("vareflow.firebase.")

    var state = CloudState.SIGNED_OUT
        private set
    var lastError: String? = null
        private set
    var membership: WebMembership? = null
        private set
    /** Garasjestudio (owners/{uid}): may create companies. */
    var isOwner = false
        private set
    /** The open company, kept live (name, short name, "name next to logo"). */
    var company: CompanyProfile? = null
        private set
    /** The open company's logo (branding/logo), or null. */
    var logo: CompanyLogo? = null
        private set
    private var core: VareflowCore? = null
    private var outboxStore: BrowserKeyValueStore? = null

    val conflicts = mutableListOf<CountConflict>()
    val rejected = mutableListOf<RejectedCountWrite>()
    var pendingWrites = 0
        private set
    private var writeInFlight = false
    /** Raised on every sign-out: results of a write sent before belong to the old session. */
    private var generation = 0
    private var signOutAfterWrite = false
    private var retryDelayMs = 3000
    /** Last server value per count (session|department|product), to restore after a refused write. */
    private val lastRemoteCounts = mutableMapOf<String, RemoteCountEntry>()

    private val listeners = mutableListOf<dynamic>()
    private val sessionListeners = mutableListOf<dynamic>()
    private var listenedSessionId: String? = null

    init {
        window.addEventListener("online", { flushOutbox() })
        window.addEventListener("offline", { if (membership != null) { state = CloudState.OFFLINE; changed() } })
    }

    private fun changed() = onChange()

    private fun ensure(): Boolean {
        if (sdk == null) {
            lastError = t("Kunne ikke koble til Vareflow. Sjekk nettforbindelsen og last siden på nytt.", "Could not connect to Vareflow. Check the network connection and reload the page.")
            return false
        }
        if (app != null) return true
        return try {
            val apps = sdk.apps
            val fresh = apps == null || (apps.length as Int) == 0
            app = if (!fresh) apps[0] else sdk.initializeApp(FirebaseWebConfig.asJs())
            // App Check must be activated before the first Auth/Firestore call (monitor mode, never blocks).
            if (fresh) activateAppCheck(app)
            auth = app.auth()
            db = app.firestore()
            // Offline copy in this browser (IndexedDB), shared between tabs. Fails quietly where unsupported.
            runCatching {
                val options: dynamic = js("({})")
                options.synchronizeTabs = true
                db.enablePersistence(options).catch { _: dynamic -> }
            }
            true
        } catch (t: Throwable) {
            lastError = t.message ?: t("Kunne ikke starte tilkoblingen.", "Could not start the connection.")
            false
        }
    }

    fun lastCompanyId(): String = prefs.getString("last_company_id").orEmpty()
    fun lastCompanyCode(): String = prefs.getString("last_company_code").orEmpty()

    // ------------------------------------------------------------------ sign-in

    fun signIn(email: String, password: String, onResult: (Result<WebSignIn>) -> Unit) {
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        connecting()
        auth.signInWithEmailAndPassword(email.trim(), password).then({ cred: dynamic ->
            val user = cred.user
            findCompanies(user.uid as String, (user.email as String?).orEmpty(), onResult)
        }, { e: dynamic -> stopLogin(IllegalStateException(friendlyAuthError(e)), onResult) })
    }

    fun signInWithGoogle(onResult: (Result<WebSignIn>) -> Unit) {
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        connecting()
        val provider: dynamic = js("new firebase.auth.GoogleAuthProvider()")
        auth.signInWithPopup(provider).then({ cred: dynamic ->
            val user = cred.user
            findCompanies(user.uid as String, (user.email as String?).orEmpty(), onResult)
        }, { e: dynamic ->
            val code = firebaseErrorCode(e)
            if (code == "popup-closed-by-user" || code == "cancelled-popup-request") {
                state = CloudState.SIGNED_OUT
                onResult(Result.failure(LoginCancelled))
            } else stopLogin(IllegalStateException(friendlyAuthError(e)), onResult)
        })
    }

    fun signInWithCompanyCode(rawCode: String, username: String, password: String, onResult: (Result<WebMembership>) -> Unit) {
        lookupCompanyCode(rawCode) { lookup ->
            val info = lookup.getOrElse { return@lookupCompanyCode onResult(Result.failure(it)) }
                ?: return@lookupCompanyCode onResult(Result.failure(IllegalStateException(t("Ukjent Firmakode.", "Unknown company code."))))
            connecting()
            val email = CompanyLogin.syntheticEmail(username, info.companyId)
            auth.signInWithEmailAndPassword(email, password).then({ cred: dynamic ->
                prefs.putStrings(mapOf("last_company_code" to info.code))
                loadMembership(info.companyId, cred.user.uid as String, email, signOutOnFailure(onResult))
            }, { e: dynamic -> stopLogin(IllegalStateException(friendlyAuthError(e, byUsername = true)), onResult) })
        }
    }

    fun lookupCompanyCode(rawCode: String, onResult: (Result<CompanyCodeInfo?>) -> Unit) {
        val code = CompanyCode.normalize(rawCode)
        if (!CompanyCode.isValid(code)) return onResult(Result.success(null))
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        db.doc(WebPaths.companyCode(code)).get().then({ doc: dynamic ->
            val data = if (doc.exists as Boolean) docToMap(doc) else emptyMap()
            val companyId = data["companyId"]?.toString().orEmpty()
            onResult(Result.success(if (companyId.isBlank()) null else CompanyCodeInfo(code, companyId, data["name"]?.toString().orEmpty())))
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(firebaseErrorMessage(e)))) })
    }

    /** Opens [companyId] for the person who is already signed in (company picker, restore). */
    fun openCompany(companyId: String, onResult: (Result<WebMembership>) -> Unit) {
        val user = auth?.currentUser
        if (user == null) return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        connecting()
        loadMembership(companyId, user.uid as String, (user.email as String?).orEmpty(), signOutOnFailure(onResult))
    }

    /**
     * Restores the login after a reload. With "Husk meg" it survives closing the browser; without
     * it, it lasts while this tab is open (a reload keeps you in, closing the tab logs you out).
     */
    fun restore(onResult: (Result<WebMembership>) -> Unit) {
        if ((prefs.getString("remember") != "true" && !tabSessionActive()) || lastCompanyId().isBlank()) {
            if (ensure()) auth.signOut()
            return onResult(Result.failure(IllegalStateException("")))
        }
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        connecting()
        var unsubscribe: dynamic = null
        unsubscribe = auth.onAuthStateChanged({ user: dynamic ->
            if (unsubscribe != null) unsubscribe()
            if (user == null) {
                state = CloudState.SIGNED_OUT
                changed()
                onResult(Result.failure(IllegalStateException("")))
            } else {
                val uid = user.uid as String
                val email = (user.email as String?).orEmpty()
                fun done(result: Result<WebMembership>) {
                    val m = result.getOrNull()
                    if (m != null && m.mustChangePassword) {
                        signOut()
                        onResult(Result.failure(IllegalStateException(t("Logg inn og velg ditt eget passord.", "Log in and choose your own password."))))
                    } else onResult(result)
                }
                db.doc("${WebPaths.memberships(lastCompanyId())}/$uid").get().then({ doc: dynamic ->
                    if (doc.exists as Boolean) loadMembership(lastCompanyId(), uid, email, ::done)
                    else findCompanies(uid, email) { found ->
                        // The remembered company is gone: open the only other one, or ask at login.
                        val ready = found.getOrNull() as? WebSignIn.Ready
                        if (ready != null) done(Result.success(ready.membership))
                        else { if (auth != null) auth.signOut(); state = CloudState.SIGNED_OUT; changed(); onResult(Result.failure(IllegalStateException(""))) }
                    }
                }, { _: dynamic -> loadMembership(lastCompanyId(), uid, email, ::done) })
            }
        })
    }

    private fun findCompanies(uid: String, email: String, onResult: (Result<WebSignIn>) -> Unit) {
        db.collectionGroup("memberships").where("uid", "==", uid).get().then({ snap: dynamic ->
            val docs = (0 until (snap.docs.length as Int)).map { snap.docs[it] }
            val active = docs.filter { (it.get("active") as Boolean?) != false }
            val companyIds = active.map { it.ref.parent.parent.id as String }.distinct()
            when {
                companyIds.isEmpty() -> loadMembership(lastCompanyId().ifBlank { "-" }, uid, email) { result ->
                    onResult(result.map { WebSignIn.Ready(it) as WebSignIn }.recoverCatching { e ->
                        throw if (e is NoMembership)
                            IllegalStateException(t("Du er ikke med i noen bedrift i Vareflow ennå. Be admin om en invitasjon.", "You are not in any company in Vareflow yet. Ask an admin for an invitation."))
                        else e
                    })
                }
                companyIds.size == 1 -> loadMembership(companyIds.first(), uid, email, signOutOnFailure { r -> onResult(r.map { WebSignIn.Ready(it) }) })
                else -> loadChoices(active, onResult)
            }
        }, { e: dynamic -> stopLogin(IllegalStateException(firebaseErrorMessage(e)), onResult) })
    }

    private fun loadChoices(memberships: List<dynamic>, onResult: (Result<WebSignIn>) -> Unit) {
        val choices = mutableListOf<CompanyChoice>()
        var remaining = memberships.size
        fun done() {
            remaining--
            if (remaining > 0) return
            val sorted = choices.sortedWith { a, b -> compareNorwegian(a.name, b.name) }
            when (sorted.size) {
                0 -> stopLogin(IllegalStateException(t("Bedriftene dine er stengt i Vareflow. Kontakt Garasjestudio.", "Your companies are closed in Vareflow. Contact Garasjestudio.")), onResult)
                1 -> openCompany(sorted.first().companyId) { r -> onResult(r.map { WebSignIn.Ready(it) }) }
                else -> { state = CloudState.SIGNED_OUT; changed(); onResult(Result.success(WebSignIn.Choose(sorted))) }
            }
        }
        memberships.forEach { m ->
            val companyId = m.ref.parent.parent.id as String
            val role = (m.get("role") as String?).orEmpty()
            db.doc(WebPaths.company(companyId)).get().then({ doc: dynamic ->
                val company = CloudMapping.companyFromCloud(companyId, docToMap(doc))
                if (company.status != CompanyStatus.SUSPENDED) choices += CompanyChoice(companyId, company.name, role)
                done()
            }, { e: dynamic ->
                // Not readable: suspended (permission denied) is left out; a network problem still lists it.
                if (firebaseErrorCode(e) != "permission-denied") choices += CompanyChoice(companyId, companyId, role)
                done()
            })
        }
    }

    private fun loadMembership(companyId: String, uid: String, email: String, onResult: (Result<WebMembership>) -> Unit) {
        db.doc("${WebPaths.memberships(companyId)}/$uid").get().then({ doc: dynamic ->
            if (!(doc.exists as Boolean)) {
                return@then stopLogin(NoMembership(t("Brukeren har ingen medlemskap i bedriften '$companyId'.", "The user has no membership in the company '$companyId'.")), onResult)
            }
            val data = docToMap(doc)
            if (data["active"] == false) return@then stopLogin(IllegalStateException(t("Brukeren er deaktivert i denne bedriften.", "The user is deactivated in this company.")), onResult)
            val accessUntil = (data["accessUntil"] as? Number)?.toLong()
            // Time-limited helper whose time is over: the rules would refuse everything anyway.
            if (HelperAccess.isExpired(accessUntil, nowMillis())) return@then stopLogin(IllegalStateException(HelperAccess.expiredMessage), onResult)
            if (data["uid"] != uid) doc.ref.update("uid", uid).catch { _: dynamic -> }
            val membership = WebMembership(
                uid = uid, companyId = companyId, companyName = companyId, companyShortName = "", companyCode = "",
                locationId = data["locationId"]?.toString().orEmpty().ifBlank { "honefoss" },
                locationName = data["locationName"]?.toString().orEmpty().ifBlank { "Hønefoss" },
                displayName = data["displayName"]?.toString().orEmpty().ifBlank { email },
                email = data["email"]?.toString().orEmpty().ifBlank { email },
                role = data["role"]?.toString().orEmpty().ifBlank { "EMPLOYEE" },
                permissions = (data["permissions"] as? List<*>)?.mapNotNull { it?.toString() }?.toSet().orEmpty(),
                active = true,
                mustChangePassword = data["mustChangePassword"] == true,
                accessUntil = accessUntil
            )
            loadCompany(membership, onResult)
        }, { e: dynamic ->
            state = CloudState.ERROR
            lastError = firebaseErrorMessage(e)
            changed()
            onResult(Result.failure(IllegalStateException(friendlyAuthError(e))))
        })
    }

    private fun loadCompany(m: WebMembership, onResult: (Result<WebMembership>) -> Unit) {
        fun finish(result: WebMembership) {
            membership = result
            prefs.putStrings(mapOf("last_company_id" to result.companyId))
            state = CloudState.ONLINE
            changed()
            onResult(Result.success(result))
        }
        db.doc(WebPaths.company(m.companyId)).get().then({ doc: dynamic ->
            val company = CloudMapping.companyFromCloud(m.companyId, docToMap(doc))
            if (company.status == CompanyStatus.SUSPENDED) {
                stopLogin(IllegalStateException(t("${company.name} er stengt i Vareflow. Kontakt Garasjestudio.", "${company.name} is closed in Vareflow. Contact Garasjestudio.")), onResult)
            } else finish(m.copy(companyName = company.name, companyShortName = company.shortName, companyCode = company.code))
        }, { e: dynamic ->
            if (firebaseErrorCode(e) == "permission-denied") {
                stopLogin(IllegalStateException(t("Bedriften er stengt i Vareflow. Kontakt Garasjestudio.", "The company is closed in Vareflow. Contact Garasjestudio.")), onResult)
            } else finish(m) // offline without a cached copy: names come later
        })
    }

    /** First login with a start code: choose an own password, then clear the flag. */
    fun changeOwnPassword(newPassword: String, onResult: (Result<Unit>) -> Unit) {
        val user = auth?.currentUser
        val m = membership
        if (user == null || m == null) return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        user.updatePassword(newPassword).then({ _: dynamic ->
            membership = m.copy(mustChangePassword = false)
            db.doc("${WebPaths.memberships(m.companyId)}/${m.uid}").update("mustChangePassword", false).catch { e: dynamic ->
                lastError = t("Passordflagg: ${firebaseErrorMessage(e)}", "Password flag: ${firebaseErrorMessage(e)}")
            }
            onResult(Result.success(Unit))
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    fun setRemember(remember: Boolean) {
        prefs.putStrings(mapOf("remember" to remember.toString()))
        setTabSession(true)
        // Firebase keeps the sign-in: for good with "Husk meg", otherwise only for this tab.
        if (ensure()) runCatching {
            val persistence = if (remember) sdk.auth.Auth.Persistence.LOCAL else sdk.auth.Auth.Persistence.SESSION
            auth.setPersistence(persistence).then({ _: dynamic -> }, { _: dynamic -> })
        }
    }

    /** Local test login: no Firebase session to keep. */
    fun forgetSession() {
        prefs.putStrings(mapOf("remember" to "false"))
        setTabSession(false)
    }

    private fun tabSessionActive(): Boolean =
        runCatching { window.sessionStorage.getItem(TAB_SESSION_KEY) == "1" }.getOrDefault(false)

    private fun setTabSession(active: Boolean) {
        runCatching { if (active) window.sessionStorage.setItem(TAB_SESSION_KEY, "1") else window.sessionStorage.removeItem(TAB_SESSION_KEY) }
    }
    fun isRemembered(): Boolean = prefs.getString("remember") == "true"

    fun signOut() {
        stopListeners()
        core?.cloudSink = null
        core = null
        stopSupportListeners()
        membership = null
        company = null
        logo = null
        conflicts.clear()
        rejected.clear()
        lastRemoteCounts.clear()
        outboxStore = null
        pendingWrites = 0
        isOwner = false
        generation++
        prefs.putStrings(mapOf("remember" to "false"))
        setTabSession(false)
        // A count transaction still running needs the sign-in to finish; sign out right after it.
        if (writeInFlight) signOutAfterWrite = true else if (auth != null) auth.signOut()
        state = CloudState.SIGNED_OUT
        changed()
    }

    private fun connecting() {
        state = CloudState.CONNECTING
        lastError = null
        changed()
    }

    private fun <T> stopLogin(error: Throwable, onResult: (Result<T>) -> Unit) {
        if (auth != null) auth.signOut()
        state = CloudState.ERROR
        lastError = error.message
        changed()
        onResult(Result.failure(error))
    }

    private fun <T> signOutOnFailure(onResult: (Result<T>) -> Unit): (Result<T>) -> Unit = { result ->
        if (result.isFailure && auth != null) auth.signOut()
        onResult(result)
    }

    private fun friendlyAuthError(error: dynamic, byUsername: Boolean = false): String = when (firebaseErrorCode(error)) {
        "invalid-credential", "invalid-login-credentials", "wrong-password", "user-not-found", "invalid-email" ->
            if (byUsername) t("Feil brukernavn eller passord.", "Wrong username or password.") else t("Feil e-post eller passord.", "Wrong e-mail or password.")
        "user-disabled" -> t("Kontoen er deaktivert. Kontakt admin.", "The account is deactivated. Contact an admin.")
        "too-many-requests" -> t("For mange forsøk. Vent litt og prøv igjen.", "Too many attempts. Wait a little and try again.")
        "network-request-failed" -> t("Ingen nettforbindelse.", "No network connection.")
        "weak-password" -> t("Passordet er for svakt. Bruk minst ${CompanyLogin.MIN_PASSWORD} tegn.", "The password is too weak. Use at least ${CompanyLogin.MIN_PASSWORD} characters.")
        "requires-recent-login" -> t("Logg inn på nytt og prøv igjen.", "Log in again and try again.")
        "email-already-in-use" -> t("Brukernavnet eller e-postadressen er allerede i bruk.", "The username or e-mail address is already in use.")
        "popup-blocked" -> t("Nettleseren stoppet Google-vinduet. Tillat popup-vinduer for denne siden og prøv igjen.", "The browser blocked the Google window. Allow pop-ups for this page and try again.")
        "unauthorized-domain" -> t("Denne adressen er ikke godkjent for innlogging. Bruk vareflow.no.", "This address is not approved for login. Use vareflow.no.")
        "operation-not-allowed" -> t("Denne innloggingsmåten er ikke slått på.", "This login method is not enabled.")
        "permission-denied" -> t("Ingen tilgang.", "No access.")
        else -> firebaseErrorMessage(error)
    }

    // ------------------------------------------------------------------ realtime

    /** Connects [target] (a cloud-mode core for the signed-in company): listeners + outgoing changes. */
    fun attach(target: VareflowCore) {
        val m = membership ?: return
        // A previously open company stops sending (its core is no longer shown).
        core?.let { if (it !== target) it.cloudSink = null }
        conflicts.clear()
        lastRemoteCounts.clear()
        core = target
        outboxStore = BrowserKeyValueStore(cloudStorePrefix(m))
        pendingWrites = loadOutbox().size
        target.applySignedInUser(m.toUserAccount())
        startListeners(target, m)
        target.cloudSink = this // also starts the listeners of the shown month
        flushOutbox()
        startSupportListeners(m.uid)
        db.doc("owners/${m.uid}").get().then({ doc: dynamic ->
            isOwner = doc.exists as Boolean
            if (isOwner) {
                startOwnerSupportListener()
                // The owner's own devices are left out of the visit counter from now on.
                runCatching { window.localStorage.setItem(VisitStats.SKIP_KEY, "1") }
            }
            if (pendingLoginVisit) { pendingLoginVisit = false; if (!isOwner) recordVisit(login = true) }
            changed()
        }, { _: dynamic ->
            isOwner = false
            if (pendingLoginVisit) { pendingLoginVisit = false; recordVisit(login = true) }
        })
    }

    // ------------------------------------------------------------------ support

    /** My own support conversations (all companies), newest first. */
    var mySupport: List<SupportTicket> = emptyList()
        private set
    /** Owner: every conversation, unread first. */
    var ownerSupport: List<SupportTicket> = emptyList()
        private set
    private var mySupportListener: dynamic = null
    private var ownerSupportListener: dynamic = null

    val unreadSupportForMe: Int get() = mySupport.count { it.unreadByUser }
    val unreadSupportForOwner: Int get() = ownerSupport.count { it.unreadByOwner }

    private fun stopSupportListeners() {
        if (mySupportListener != null) runCatching { mySupportListener() }
        if (ownerSupportListener != null) runCatching { ownerSupportListener() }
        mySupportListener = null
        ownerSupportListener = null
        mySupport = emptyList()
        ownerSupport = emptyList()
    }

    private fun startSupportListeners(uid: String) {
        if (mySupportListener != null) runCatching { mySupportListener() }
        mySupportListener = db.collection("support").where("uid", "==", uid).onSnapshot({ snap: dynamic ->
            mySupport = docsOf(snap).map { CloudMapping.supportTicketFromCloud(docToMap(it)) }
                .filter { it.uid == uid }.sortedByDescending { it.updatedAt }
            changed()
        }, { _: dynamic -> })
    }

    private fun startOwnerSupportListener() {
        if (ownerSupportListener != null) return
        ownerSupportListener = db.collection("support").onSnapshot({ snap: dynamic ->
            ownerSupport = SupportRules.ownerOrder(docsOf(snap).map { CloudMapping.supportTicketFromCloud(docToMap(it)) })
            changed()
        }, { _: dynamic -> })
    }

    /** Mer → Support → new conversation: the ticket and its first message in one batch. */
    fun createSupportTicket(category: SupportCategory, subject: String, text: String, onResult: (Result<String>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        SupportRules.problem(subject, text)?.let { return onResult(Result.failure(IllegalStateException(it))) }
        val ticketRef = db.collection("support").doc()
        val ticket = SupportTicket(
            id = ticketRef.id as String, companyId = m.companyId, companyName = company?.name ?: m.companyName,
            uid = m.uid, userName = m.displayName, userEmail = m.email, category = category, subject = subject,
            appVersion = WEB_VERSION, platform = "web", language = I18n.language.code
        )
        val batch = db.batch()
        batch.set(ticketRef, kotlinToJs(CloudMapping.supportTicketToCloud(ticket) + mapOf("createdAt" to ServerTime, "updatedAt" to ServerTime)))
        batch.set(ticketRef.collection("messages").doc(), kotlinToJs(CloudMapping.supportMessageToCloud(
            SupportMessage("", SupportMessage.FROM_USER, m.displayName, text)) + ("createdAt" to ServerTime)))
        batch.commit().then({ _: dynamic -> onResult(Result.success(ticket.id)) },
            { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** The messages of one conversation, live; call the returned function to stop. */
    fun listenSupportMessages(ticketId: String, onMessages: (List<SupportMessage>) -> Unit): () -> Unit {
        val stop: dynamic = db.collection("support/$ticketId/messages").onSnapshot({ snap: dynamic ->
            onMessages(docsOf(snap).map { CloudMapping.supportMessageFromCloud(docToMap(it)) }.sortedBy { it.createdAt })
        }, { _: dynamic -> })
        return { runCatching { stop() } }
    }

    /** An answer in a conversation: from the owner (Eier page) or from the person who wrote it. */
    fun replySupport(ticket: SupportTicket, text: String, asOwner: Boolean, onResult: (Result<Unit>) -> Unit) {
        SupportRules.problem(null, text)?.let { return onResult(Result.failure(IllegalStateException(it))) }
        val name = if (asOwner) "Garasjestudio" else membership?.displayName ?: ticket.userName
        val ticketRef = db.doc("support/${ticket.id}")
        val batch = db.batch()
        batch.set(ticketRef.collection("messages").doc(), kotlinToJs(CloudMapping.supportMessageToCloud(
            SupportMessage("", if (asOwner) SupportMessage.FROM_OWNER else SupportMessage.FROM_USER, name, text)) + ("createdAt" to ServerTime)))
        batch.update(ticketRef, kotlinToJs(
            if (asOwner) mapOf("status" to SupportStatus.ANSWERED.name, "unreadByUser" to true, "unreadByOwner" to false, "updatedAt" to ServerTime)
            else mapOf("status" to SupportStatus.OPEN.name, "unreadByOwner" to true, "unreadByUser" to false, "updatedAt" to ServerTime)))
        batch.commit().then({ _: dynamic -> onResult(Result.success(Unit)) },
            { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** Opened: the other side's new message is read. */
    fun markSupportRead(ticket: SupportTicket, asOwner: Boolean) {
        val field = if (asOwner) "unreadByOwner" else "unreadByUser"
        val unread = if (asOwner) ticket.unreadByOwner else ticket.unreadByUser
        if (!unread) return
        db.doc("support/${ticket.id}").update(kotlinToJs(mapOf(field to false))).then({ _: dynamic -> }, { _: dynamic -> })
    }

    /** Owner: close a conversation, or open it again. */
    fun setSupportClosed(ticket: SupportTicket, closed: Boolean, onResult: (Result<Unit>) -> Unit) {
        val status = if (closed) SupportStatus.CLOSED else SupportStatus.ANSWERED
        db.doc("support/${ticket.id}").update(kotlinToJs(mapOf("status" to status.name, "unreadByOwner" to false, "updatedAt" to ServerTime)))
            .then({ _: dynamic -> onResult(Result.success(Unit)) }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** The companies the signed-in person belongs to (for "Bytt bedrift"). */
    fun listCompanies(onResult: (Result<List<CompanyChoice>>) -> Unit) {
        val user = auth?.currentUser ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        db.collectionGroup("memberships").where("uid", "==", user.uid).get().then({ snap: dynamic ->
            val docs = docsOf(snap).filter { (it.get("active") as Boolean?) != false }
            if (docs.isEmpty()) return@then onResult(Result.success(emptyList()))
            val choices = mutableListOf<CompanyChoice>()
            var remaining = docs.size
            docs.forEach { d ->
                val companyId = d.ref.parent.parent.id as String
                val role = (d.get("role") as String?).orEmpty()
                db.doc(WebPaths.company(companyId)).get().then({ doc: dynamic ->
                    val company = CloudMapping.companyFromCloud(companyId, docToMap(doc))
                    if (company.status != CompanyStatus.SUSPENDED) choices += CompanyChoice(companyId, company.name, role)
                    if (--remaining == 0) onResult(Result.success(choices.sortedWith { a, b -> compareNorwegian(a.name, b.name) }))
                }, { _: dynamic ->
                    if (--remaining == 0) onResult(Result.success(choices.sortedWith { a, b -> compareNorwegian(a.name, b.name) }))
                })
            }
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(firebaseErrorMessage(e)))) })
    }

    /**
     * New company (owner only, same as the phone's "Ny bedrift"): company with a free Firmakode,
     * its location and the owner as first admin, in one transaction. Starts empty.
     */
    fun createCompany(name: String, locationName: String, onResult: (Result<WebMembership>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        val user = auth?.currentUser ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        val uid = user.uid as String
        val companyId = companySlug(name)
        val locationId = companySlug(locationName).ifBlank { "hoved" }
        val companyRef = db.doc(WebPaths.company(companyId))
        db.runTransaction({ tx: dynamic ->
            tx.get(companyRef).then({ existing: dynamic ->
                if (existing.exists as Boolean) throw IllegalStateException(t("Det finnes allerede en bedrift med id «$companyId».", "A company with id «$companyId» already exists."))
                freeCode(tx, name, mutableSetOf(), 0).then({ code: dynamic ->
                    val codeText = code as String
                    val company = CompanyProfile(id = companyId, name = name, code = codeText)
                    tx.set(db.doc(WebPaths.companyCode(codeText)), kotlinToJs(CloudMapping.companyCodeToCloud(company) + ("createdAt" to ServerTime)))
                    tx.set(companyRef, kotlinToJs(CloudMapping.companyToCloud(company) + mapOf(
                        "locale" to "nb-NO", "currency" to "NOK", "createdBy" to uid, "createdAt" to ServerTime
                    )))
                    tx.set(db.doc("${WebPaths.company(companyId)}/locations/$locationId"), kotlinToJs(mapOf(
                        "name" to locationName, "active" to true, "createdAt" to ServerTime
                    )))
                    tx.set(db.doc("${WebPaths.memberships(companyId)}/$uid"), kotlinToJs(mapOf(
                        "uid" to uid, "displayName" to m.displayName, "email" to m.email, "role" to "ADMIN",
                        "permissions" to listOf("*"), "active" to true, "theme" to "GLOD",
                        "locationId" to locationId, "locationName" to locationName, "createdAt" to ServerTime
                    )))
                    codeText
                })
            })
        }).then({ _: dynamic -> openCompany(companyId, onResult) },
            { e: dynamic -> onResult(Result.failure(IllegalStateException(e.message as String? ?: firebaseErrorMessage(e)))) })
    }

    // ------------------------------------------------------------------ besøksteller (visit counter)

    /** Set by the login page: the next signed-in company start is a real login (not a reload). */
    var pendingLoginVisit = false

    /**
     * Adds 1 to today's counter: a page view (once per tab) or a login. Nothing personal is stored.
     * Not counted: the owner's devices, the `?test` mode, and localhost (unless `?statstest`).
     */
    fun recordVisit(login: Boolean) {
        if (!ensure()) return
        val skip = runCatching {
            val host = window.location.hostname as String
            val search = window.location.search as String
            window.localStorage.getItem(VisitStats.SKIP_KEY) == "1" || webTestMode ||
                ((host == "localhost" || host == "127.0.0.1") && !search.contains("statstest"))
        }.getOrDefault(true)
        if (skip) return
        if (!login) {
            val counted = runCatching { window.sessionStorage.getItem(VisitStats.COUNTED_KEY) == "1" }.getOrDefault(false)
            if (counted) return
            runCatching { window.sessionStorage.setItem(VisitStats.COUNTED_KEY, "1") }
        }
        fun inc(n: Int): dynamic = firebaseSdk().firestore.FieldValue.increment(n)
        val date = VisitStats.osloDate()
        val data: dynamic = js("({})")
        data.date = date
        if (login) {
            data.logins = inc(1)
        } else {
            data.pageViews = inc(1)
            val countries: dynamic = js("({})")
            countries[VisitStats.countryOfThisBrowser()] = inc(1)
            data.countries = countries
            val devices: dynamic = js("({})")
            devices[VisitStats.deviceOfThisBrowser()] = inc(1)
            data.devices = devices
        }
        runCatching {
            db.collection("stats").doc("visits").collection("days").doc(date).set(data, mergeOption())
                .catch { _: dynamic -> } // counting must never disturb the app
        }
    }

    /** The last [days] days of visits (owner only), oldest first; missing days count as 0. */
    fun loadVisitDays(days: Int, onResult: (Result<List<VisitDay>>) -> Unit) {
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError ?: "")))
        val dates = VisitStats.lastDates(days)
        db.collection("stats").doc("visits").collection("days").where("date", ">=", dates.first()).get().then({ snap: dynamic ->
            val found = docsOf(snap).map { doc ->
                val map = docToMap(doc)
                fun counts(key: String): Map<String, Int> =
                    (map[key] as? Map<*, *>)?.entries?.associate { it.key.toString() to ((it.value as? Number)?.toInt() ?: 0) } ?: emptyMap()
                VisitDay(map["date"]?.toString() ?: (doc.id as String), (map["pageViews"] as? Number)?.toInt() ?: 0,
                    (map["logins"] as? Number)?.toInt() ?: 0, counts("countries"), counts("devices"))
            }.associateBy { it.date }
            onResult(Result.success(dates.map { found[it] ?: VisitDay(it, 0, 0, emptyMap(), emptyMap()) }))
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    // ------------------------------------------------------------------ Eier-siden (owner only)

    /** All companies (only the owner may list them) and the owner's own role in each. */
    fun listAllCompanies(onResult: (Result<List<OwnerCompanyRow>>) -> Unit) {
        val user = auth?.currentUser ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        db.collection("companies").get().then({ snap: dynamic ->
            val companies = docsOf(snap).map { doc ->
                val map = docToMap(doc)
                Triple(CloudMapping.companyFromCloud(doc.id as String, map), (map["createdAt"] as? Number)?.toLong(),
                    map["suspendedReason"]?.toString().orEmpty() to map["suspendedNote"]?.toString().orEmpty())
            }
            db.collectionGroup("memberships").where("uid", "==", user.uid).get().then({ mine: dynamic ->
                val roles = docsOf(mine).filter { (it.get("active") as Boolean?) != false }
                    .associate { (it.ref.parent.parent.id as String) to (it.get("role") as String?).orEmpty() }
                onResult(Result.success(companies.map { (c, created, why) -> OwnerCompanyRow(c, created, roles[c.id], why.first, why.second) }
                    .sortedWith { a, b -> compareNorwegian(a.company.name, b.company.name) }))
            }, { _: dynamic ->
                onResult(Result.success(companies.map { (c, created, why) -> OwnerCompanyRow(c, created, null, why.first, why.second) }))
            })
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** Suspend (closed for everyone in it, data kept) or open a company again. [reason]: PAYMENT or OTHER. */
    fun setCompanySuspended(companyId: String, suspended: Boolean, reason: String, note: String, onResult: (Result<Unit>) -> Unit) {
        val status = if (suspended) CompanyStatus.SUSPENDED.name else CompanyStatus.ACTIVE.name
        val changes = if (suspended) mapOf("status" to status, "suspendedReason" to reason, "suspendedNote" to note.trim().take(200), "updatedAt" to ServerTime)
            else mapOf("status" to status, "suspendedReason" to "", "suspendedNote" to "", "updatedAt" to ServerTime)
        db.doc(WebPaths.company(companyId)).update(kotlinToJs(changes))
            .then({ _: dynamic -> onResult(Result.success(Unit)) }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** A new free Firmakode for the company; the old one stops working at once. Returns the new code. */
    fun changeCompanyCode(companyId: String, onResult: (Result<String>) -> Unit) {
        val companyRef = db.doc(WebPaths.company(companyId))
        db.runTransaction({ tx: dynamic ->
            tx.get(companyRef).then({ doc: dynamic ->
                if (!(doc.exists as Boolean)) throw IllegalStateException(t("Fant ikke bedriften.", "Company not found."))
                val company = CloudMapping.companyFromCloud(companyId, docToMap(doc))
                freeCode(tx, company.name, mutableSetOf(company.code), 0).then({ code: dynamic ->
                    val newCode = code as String
                    tx.set(db.doc(WebPaths.companyCode(newCode)), kotlinToJs(CloudMapping.companyCodeToCloud(company) + ("createdAt" to ServerTime)))
                    tx.update(companyRef, kotlinToJs(mapOf("code" to newCode, "updatedAt" to ServerTime)))
                    if (company.code.isNotBlank()) tx.delete(db.doc(WebPaths.companyCode(company.code)))
                    newCode
                })
            })
        }).then({ code: dynamic -> onResult(Result.success(code as String)) },
            { e: dynamic -> onResult(Result.failure(IllegalStateException(e.message as String? ?: firebaseErrorMessage(e)))) })
    }

    /** First suggested Firmakode that is free, read inside the transaction (at most 8 tries). */
    private fun freeCode(tx: dynamic, name: String, tried: MutableSet<String>, attempt: Int): dynamic {
        if (attempt >= 8) throw IllegalStateException(t("Fant ingen ledig Firmakode. Prøv igjen.", "Found no free company code. Try again."))
        val candidate = CompanyCode.suggest(name, tried)
        return tx.get(db.doc(WebPaths.companyCode(candidate))).then({ doc: dynamic ->
            if (doc.exists as Boolean) { tried += candidate; freeCode(tx, name, tried, attempt + 1) } else candidate
        })
    }

    private fun startListeners(c: VareflowCore, m: WebMembership) {
        stopListeners()
        listeners.add(db.collection(WebPaths.products(m.companyId)).onSnapshot({ snap: dynamic ->
            val removed = changesOf(snap, "removed").map { it.doc.id as String }
            if (removed.isNotEmpty()) c.removeCloudProducts(removed)
            // Only what changed since the last snapshot (the first snapshot has every product as "added").
            val changedDocs = (changesOf(snap, "added") + changesOf(snap, "modified")).map { it.doc }
            if (changedDocs.isNotEmpty()) c.applyCloudProducts(changedDocs.map { docToMap(it) })
            val docs = docsOf(snap)
            // The server's full list: drop what was deleted elsewhere while this browser was away.
            if (!(snap.metadata.fromCache as Boolean)) c.retainCloudProducts(docs.map { it.id as String }.toSet())
            markSnapshot(snap)
        }, { e: dynamic -> listenerError(e) }))
        // Company name and logo for the header, Mer → Bedrift and reports.
        listeners.add(db.doc(WebPaths.company(m.companyId)).onSnapshot({ doc: dynamic ->
            if (doc.exists as Boolean) {
                val profile = CloudMapping.companyFromCloud(m.companyId, docToMap(doc))
                company = profile
                membership = membership?.copy(companyName = profile.name, companyShortName = profile.shortName, companyCode = profile.code)
                changed()
            }
        }, { e: dynamic -> listenerError(e) }))
        listeners.add(db.doc(WebPaths.logo(m.companyId)).onSnapshot({ doc: dynamic ->
            @Suppress("UNCHECKED_CAST")
            logo = if (doc.exists as Boolean) CloudMapping.logoFromCloud(jsToKotlin(doc.data()) as? Map<String, Any?>) else null
            changed()
        }, { _: dynamic -> logo = null; changed() }))
        listeners.add(db.collection(WebPaths.memberships(m.companyId)).onSnapshot({ snap: dynamic ->
            c.applyCloudMemberships(docsOf(snap).map { docToMap(it) })
            // My own role/permissions may have changed.
            val own: dynamic = docsOf(snap).firstOrNull { it.id == m.uid }
            if (own != null) ownMembershipChanged(docToMap(own))
        }, { e: dynamic -> listenerError(e) }))
        // The own membership on its own: always readable (also after a helper's time is over), so a
        // closed or shortened access is noticed even when the company listeners are refused.
        listeners.add(db.doc("${WebPaths.memberships(m.companyId)}/${m.uid}").onSnapshot({ doc: dynamic ->
            if (doc.exists as Boolean) ownMembershipChanged(docToMap(doc))
        }, { _: dynamic -> }))
        // With metadata changes, so "from cache → from server" is noticed even when no data changed
        // (drives the state pill and the retry of queued counts).
        val withMetadata: dynamic = js("({ includeMetadataChanges: true })")
        listeners.add(db.collection(WebPaths.sessions(m.companyId, m.locationId)).onSnapshot(withMetadata, { snap: dynamic ->
            if ((snap.docChanges().length as Int) > 0) c.applyCloudSessions(docsOf(snap).map { docToMap(it) })
            markSnapshot(snap)
        }, { e: dynamic -> listenerError(e) }))
        // Historikk, hovedvarer (also empty ones, with order), stikkprøver and – for admins – the log.
        listeners.add(db.collection(WebPaths.history(m.companyId, m.locationId)).onSnapshot({ snap: dynamic ->
            val changed = (changesOf(snap, "added") + changesOf(snap, "modified")).map { docToMap(it.doc) }
            c.applyCloudHistory(changed)
        }, { e: dynamic -> listenerError(e) }))
        listeners.add(db.collection(WebPaths.categories(m.companyId)).onSnapshot(withMetadata, { snap: dynamic ->
            val changed = (changesOf(snap, "added") + changesOf(snap, "modified")).map { docToMap(it.doc) }
            val removed = changesOf(snap, "removed").map { it.doc.id as String }
            val complete = !(snap.metadata.fromCache as Boolean)
            if (changed.isNotEmpty() || removed.isNotEmpty() || complete) c.applyCloudCategories(changed, removed, complete)
        }, { e: dynamic -> listenerError(e) }))
        listeners.add(db.collection(WebPaths.svinn(m.companyId, m.locationId)).onSnapshot({ snap: dynamic ->
            val changed = (changesOf(snap, "added") + changesOf(snap, "modified")).map { docToMap(it.doc) }
            val removed = changesOf(snap, "removed").map { it.doc.id as String }
            if (changed.isNotEmpty() || removed.isNotEmpty()) c.applyCloudSvinn(changed, removed)
        }, { e: dynamic -> listenerError(e) }))
        val me = m.toUserAccount()
        if (me.role == Role.ADMIN || AccessRules.hasPermission(me, Permission.AUDIT_VIEW)) {
            listeners.add(db.collection(WebPaths.changeEvents(m.companyId)).orderBy("timestamp", "desc").limit(500).onSnapshot({ snap: dynamic ->
                c.applyCloudAudit((changesOf(snap, "added") + changesOf(snap, "modified")).map { docToMap(it.doc) })
            }, { e: dynamic -> console.warn("Logg: " + (e?.message ?: e)) }))
        }
    }

    /** Deactivated, or a helper whose time an admin closed or shortened: back to the login page. */
    private fun ownMembershipChanged(data: Map<String, Any?>) {
        if (membership == null) return
        if (data["active"] == false) {
            signOut()
            onSignedOut(t("Brukeren er deaktivert i denne bedriften.", "The user is deactivated in this company."))
            return
        }
        val until = (data["accessUntil"] as? Number)?.toLong()
        if (membership?.accessUntil != until) { membership = membership?.copy(accessUntil = until); changed() }
        if (HelperAccess.isExpired(until, nowMillis())) {
            signOut()
            onSignedOut(HelperAccess.expiredMessage)
        }
    }

    override fun sessionSelected(sessionId: String) {
        val m = membership ?: return
        val c = core ?: return
        if (listenedSessionId == sessionId && sessionListeners.isNotEmpty()) return
        stopSessionListeners()
        listenedSessionId = sessionId
        sessionListeners.add(db.collection(WebPaths.assignments(m.companyId, m.locationId, sessionId)).onSnapshot({ snap: dynamic ->
            val removed = changesOf(snap, "removed").mapNotNull { it.doc.get("category") as String? }
            if (removed.isNotEmpty()) c.removeCloudAssignments(sessionId, removed)
            c.applyCloudAssignments(docsOf(snap).map { docToMap(it) })
        }, { e: dynamic -> listenerError(e) }))
        sessionListeners.add(db.collection(WebPaths.countEntries(m.companyId, m.locationId, sessionId)).onSnapshot({ snap: dynamic ->
            val entries = docsOf(snap).map { doc ->
                val d = docToMap(doc)
                RemoteCountEntry(
                    sessionId = sessionId,
                    productId = d["productId"]?.toString() ?: (doc.id as String),
                    departmentId = d["departmentId"]?.toString() ?: "bar",
                    quantity = (d["quantity"] as? Number)?.toDouble(),
                    note = d["note"]?.toString().orEmpty(),
                    updatedBy = d["updatedBy"]?.toString().orEmpty(),
                    updatedAt = (d["updatedAt"] as? Number)?.toLong() ?: (d["updatedAtLocal"] as? Number)?.toLong() ?: 0L,
                    version = (d["version"] as? Number)?.toInt() ?: 0,
                    lastEventId = d["lastEventId"]?.toString()
                )
            }.groupBy { "${it.departmentId}:${it.productId}" }.values
                .mapNotNull { same -> same.maxWithOrNull(compareBy<RemoteCountEntry> { it.version }.thenBy { it.updatedAt }) }
            entries.forEach { lastRemoteCounts[countKey(it.sessionId, it.departmentId, it.productId)] = it }
            c.applyCloudCountEntries(entries)
            markSnapshot(snap)
        }, { e: dynamic -> listenerError(e) }))
        sessionListeners.add(db.collection(WebPaths.spotChecks(m.companyId, m.locationId, sessionId)).onSnapshot({ snap: dynamic ->
            c.applyCloudSpotChecks((changesOf(snap, "added") + changesOf(snap, "modified")).map { docToMap(it.doc) })
        }, { e: dynamic -> console.warn("Stikkprøver: " + (e?.message ?: e)) }))
        sessionListeners.add(db.collection(WebPaths.conflicts(m.companyId, m.locationId, sessionId)).onSnapshot({ snap: dynamic ->
            // Only people who may resolve someone else's conflict see them all (rules: conflicts delete).
            val user = m.toUserAccount()
            val seesAll = user.role != Role.EMPLOYEE || AccessRules.hasPermission(user, Permission.INVENTORY_CLOSE)
            val incoming = docsOf(snap).mapNotNull { doc -> conflictFrom(docToMap(doc), sessionId) }
                .filter { seesAll || it.localActorId == m.uid }
            conflicts.clear()
            conflicts.addAll(incoming.sortedByDescending { it.createdAt })
            changed()
        }, { e: dynamic -> listenerError(e) }))
    }

    private fun conflictFrom(d: Map<String, Any?>, sessionFallback: String): CountConflict? {
        val productId = d["productId"]?.toString() ?: return null
        val id = d["id"].toString()
        return CountConflict(
            id = id,
            sessionId = d["sessionId"]?.toString() ?: sessionFallback,
            productId = productId,
            departmentId = d["departmentId"]?.toString() ?: "bar",
            productName = d["productName"]?.toString().orEmpty().ifBlank { productId },
            localQuantity = (d["localQuantity"] as? Number)?.toDouble(),
            localNote = d["localNote"]?.toString().orEmpty(),
            localActorId = d["localActorId"]?.toString().orEmpty(),
            localActorName = d["localActorName"]?.toString().orEmpty(),
            localBaseVersion = (d["localBaseVersion"] as? Number)?.toInt() ?: 0,
            remoteQuantity = (d["remoteQuantity"] as? Number)?.toDouble(),
            remoteNote = d["remoteNote"]?.toString().orEmpty(),
            remoteActorId = d["remoteActorId"]?.toString().orEmpty(),
            remoteVersion = (d["remoteVersion"] as? Number)?.toInt() ?: 0,
            eventId = d["eventId"]?.toString().orEmpty().ifBlank { id },
            createdAt = (d["createdAt"] as? Number)?.toLong() ?: (d["createdAtLocal"] as? Number)?.toLong() ?: 0L
        )
    }

    private fun docsOf(snap: dynamic): List<dynamic> = (0 until (snap.docs.length as Int)).map { snap.docs[it] }

    private fun changesOf(snap: dynamic, type: String): List<dynamic> {
        val changes = snap.docChanges()
        return (0 until (changes.length as Int)).map { changes[it] }.filter { it.type == type }
    }

    private fun markSnapshot(snap: dynamic) {
        val fromCache = snap.metadata.fromCache as Boolean
        val next = if (fromCache) CloudState.OFFLINE else CloudState.ONLINE
        if (next != state) { state = next; changed() }
        if (!fromCache && pendingWrites > 0) flushOutbox()
    }

    private fun listenerError(e: dynamic) {
        lastError = firebaseErrorMessage(e)
        state = CloudState.ERROR
        changed()
    }

    private fun stopSessionListeners() {
        sessionListeners.forEach { runCatching { it() } }
        sessionListeners.clear()
        listenedSessionId = null
    }

    private fun stopListeners() {
        listeners.forEach { runCatching { it() } }
        listeners.clear()
        stopSessionListeners()
    }

    // ------------------------------------------------------------------ writes (CloudSink)

    private fun write(path: String, data: Map<String, Any?>, onSettled: (() -> Unit)? = null) {
        db.doc(path).set(kotlinToJs(data), mergeOption())
            .then({ _: dynamic -> onSettled?.invoke() }, { e: dynamic -> onSettled?.invoke(); writeFailed(e) })
    }

    // ------------------------------------------------------------------ upload progress per hovedvare

    /** Product writes per hovedvare since the last time that hovedvare had nothing pending. */
    class UploadProgress(var total: Int = 0, var done: Int = 0, var finishedAt: Long = 0L) {
        val percent: Int get() = if (total == 0) 100 else (done * 100 / total)
        val finished: Boolean get() = done >= total
    }

    private val productUploads = mutableMapOf<String, UploadProgress>()

    /** The upload state of a hovedvare, while it is uploading and for a few seconds after (to show "✓ lastet opp"). */
    fun uploadProgress(category: String): UploadProgress? {
        val p = productUploads[category] ?: return null
        if (p.finished && nowMillis() - p.finishedAt > UPLOAD_DONE_VISIBLE_MS) { productUploads.remove(category); return null }
        return p
    }

    private fun trackProductWrite(category: String): () -> Unit {
        val p = productUploads[category]?.takeIf { !it.finished } ?: UploadProgress().also { productUploads[category] = it }
        p.total++
        return {
            p.done++
            if (p.finished) {
                p.finishedAt = nowMillis()
                // Hide the "✓" line again after a moment.
                setTimeout(UPLOAD_DONE_VISIBLE_MS.toInt() + 50) { changed() }
            }
            changed()
        }
    }

    private fun remove(path: String) {
        db.doc(path).delete().catch { e: dynamic -> writeFailed(e) }
    }

    private fun writeFailed(e: dynamic) {
        lastError = friendlyAuthError(e)
        state = CloudState.ERROR
        changed()
    }

    /**
     * Sends only the fields that differ from the last synchronized version (with merge), so a stale
     * copy here never overwrites what someone else changed, and the rules' "only these fields"
     * checks for non-admins hold. New documents are written whole.
     */
    private fun writeChanges(path: String, current: Map<String, Any?>, previous: Map<String, Any?>?, track: (() -> (() -> Unit))? = null) {
        if (previous == null) return write(path, current, track?.invoke())
        val changed = current.filter { (key, value) -> key != "updatedAtLocal" && previous[key] != value }
        if (changed.isEmpty()) return
        write(path, changed + (if ("updatedAtLocal" in current) mapOf("updatedAtLocal" to current["updatedAtLocal"]) else emptyMap()), track?.invoke())
    }

    override fun upsertProduct(product: Product, previous: Product?) {
        val m = membership ?: return
        val now = nowMillis()
        writeChanges("${WebPaths.products(m.companyId)}/${product.id}",
            CloudMapping.productToCloud(product, m.companyId, now), previous?.let { CloudMapping.productToCloud(it, m.companyId, now) }) {
            trackProductWrite(product.category)
        }
    }

    override fun upsertHistory(snapshot: CountSession) {
        val m = membership ?: return
        write("${WebPaths.history(m.companyId, m.locationId)}/${CloudMapping.historyDocumentId(snapshot)}",
            CloudMapping.historyToCloud(snapshot.copy(companyId = m.companyId, locationId = m.locationId)) + ("updatedAt" to ServerTime))
    }

    override fun upsertCategory(documentId: String, doc: CategoryDoc) {
        val m = membership ?: return
        write("${WebPaths.categories(m.companyId)}/$documentId", CloudMapping.categoryToCloud(doc, nowMillis()))
    }

    override fun deleteCategory(documentId: String) {
        val m = membership ?: return
        remove("${WebPaths.categories(m.companyId)}/$documentId")
    }

    override fun addAudit(event: AuditEvent) {
        val m = membership ?: return
        write("${WebPaths.changeEvents(m.companyId)}/${event.id}", CloudMapping.auditToCloud(event.copy(companyId = m.companyId)) + ("serverTimestamp" to ServerTime))
    }

    override fun addSpotCheck(record: SpotCheckRecord) {
        val m = membership ?: return
        val sessionId = record.sessionId ?: return
        write("${WebPaths.spotChecks(m.companyId, m.locationId, sessionId)}/${record.id}", CloudMapping.spotCheckToCloud(record))
    }

    override fun upsertSvinn(record: SvinnRecord) {
        val m = membership ?: return
        write("${WebPaths.svinn(m.companyId, m.locationId)}/${record.id}", SvinnMapping.toCloud(record))
    }

    override fun deleteSvinn(recordId: String) {
        val m = membership ?: return
        remove("${WebPaths.svinn(m.companyId, m.locationId)}/$recordId")
    }

    override fun deleteProduct(productId: String) {
        val m = membership ?: return
        remove("${WebPaths.products(m.companyId)}/$productId")
    }

    override fun upsertSession(session: InventorySession, departments: List<InventoryDepartment>, previous: Pair<InventorySession, List<InventoryDepartment>>?) {
        val m = membership ?: return
        val now = nowMillis()
        writeChanges(
            "${WebPaths.sessions(m.companyId, m.locationId)}/${session.id}",
            CloudMapping.inventorySessionToCloud(session, departments, m.companyId, m.locationId, m.locationName, now),
            previous?.let { CloudMapping.inventorySessionToCloud(it.first, it.second, m.companyId, m.locationId, m.locationName, now) }
        )
    }

    override fun upsertAssignment(assignment: CategoryAssignment, previous: CategoryAssignment?) {
        val m = membership ?: return
        writeChanges(
            "${WebPaths.assignments(m.companyId, m.locationId, assignment.sessionId)}/${CloudMapping.safeDocumentId(assignment.category)}",
            CloudMapping.assignmentToCloud(assignment), previous?.let { CloudMapping.assignmentToCloud(it) }
        )
    }

    override fun deleteAssignment(sessionId: String, category: String) {
        val m = membership ?: return
        remove("${WebPaths.assignments(m.companyId, m.locationId, sessionId)}/${CloudMapping.safeDocumentId(category)}")
    }

    override fun upsertMembership(user: UserAccount, previous: UserAccount?) {
        val m = membership ?: return
        val now = nowMillis()
        // Compared with the same location, so the person's own location is never overwritten.
        writeChanges("${WebPaths.memberships(m.companyId)}/${user.id}",
            CloudMapping.membershipToCloud(user, m.locationId, m.locationName, now),
            previous?.let { CloudMapping.membershipToCloud(it, m.locationId, m.locationName, now) })
    }

    // ------------------------------------------------------------------ counts: outbox + transaction

    private fun loadOutbox(): List<PendingCountWrite> = CountOutboxCodec.fromJson(outboxStore?.getString(StorageKeys.COUNT_OUTBOX))

    private fun saveOutbox(items: List<PendingCountWrite>) {
        outboxStore?.putStrings(mapOf(StorageKeys.COUNT_OUTBOX to CountOutboxCodec.toJson(items)))
        pendingWrites = items.size
    }

    override fun queueCountWrite(write: PendingCountWrite) {
        saveOutbox(CountOutbox.enqueue(loadOutbox(), write))
        changed()
        flushOutbox()
    }

    fun flushOutbox() {
        if (writeInFlight || membership == null || db == null || auth?.currentUser == null) return
        val next = loadOutbox().firstOrNull()
        if (next == null) {
            pendingWrites = 0
            if (state != CloudState.ERROR) state = CloudState.ONLINE
            changed()
            return
        }
        writeInFlight = true
        sendCountWrite(next)
    }

    private fun sendCountWrite(write: PendingCountWrite) {
        val sessionPath = "${WebPaths.sessions(write.companyId, write.locationId)}/${write.sessionId}"
        val entryRef = db.doc("$sessionPath/countEntries/${CloudMapping.countEntryDocumentId(write.departmentId, write.productId)}")
        val eventRef = db.doc("${WebPaths.changeEvents(write.companyId)}/${write.eventId}")
        val conflictRef = db.doc("$sessionPath/conflicts/${write.resolutionOfConflictId ?: write.eventId}")

        val sentIn = generation
        db.runTransaction({ tx: dynamic ->
            // All reads first: the entry and (for a retried conflict) the conflict document.
            tx.get(entryRef).then({ current: dynamic -> tx.get(conflictRef).then({ existingConflict: dynamic ->
                val exists = current.exists as Boolean
                val d = if (exists) docToMap(current) else emptyMap()
                val currentVersion = (d["version"] as? Number)?.toInt() ?: 0
                val remoteQuantity = (d["quantity"] as? Number)?.toDouble()
                val remoteNote = d["note"]?.toString().orEmpty()
                // Same decision as on the phone (CountOutbox.decide, unit tested).
                when (val decision = CountOutbox.decide(write, exists, currentVersion, d["lastEventId"]?.toString(), remoteQuantity, remoteNote)) {
                    is CountOutbox.Decision.AlreadyApplied -> WriteOutcome.Ack(decision.version)
                    is CountOutbox.Decision.AlreadyEqual -> WriteOutcome.Ack(decision.version)
                    is CountOutbox.Decision.Conflict -> {
                        // Already recorded by an earlier try whose answer was lost: conflicts are never updated.
                        if (!(existingConflict.exists as Boolean)) tx.set(conflictRef, kotlinToJs(mapOf(
                            "sessionId" to write.sessionId, "productId" to write.productId, "departmentId" to write.departmentId,
                            "localQuantity" to write.quantity, "localNote" to write.note,
                            "localActorId" to write.actorUserId, "localActorName" to write.actorName,
                            "localBaseVersion" to write.baseVersion,
                            "remoteQuantity" to remoteQuantity, "remoteNote" to remoteNote,
                            "remoteActorId" to d["updatedBy"]?.toString().orEmpty(), "remoteVersion" to currentVersion,
                            "eventId" to write.eventId, "createdAt" to ServerTime, "createdAtLocal" to nowMillis(), "status" to "OPEN"
                        )), mergeOption())
                        WriteOutcome.Conflict(currentVersion, remoteQuantity, remoteNote, d["updatedBy"]?.toString().orEmpty())
                    }
                    is CountOutbox.Decision.Apply -> {
                        val newVersion = decision.newVersion
                        tx.set(entryRef, kotlinToJs(mapOf(
                            "productId" to write.productId, "departmentId" to write.departmentId,
                            "quantity" to write.quantity, "note" to write.note,
                            "updatedBy" to write.actorUserId, "updatedByName" to write.actorName,
                            "updatedAt" to ServerTime, "updatedAtLocal" to write.createdAt,
                            "version" to newVersion, "lastEventId" to write.eventId
                        )), mergeOption())
                        tx.set(eventRef, kotlinToJs(mapOf(
                            "eventId" to write.eventId, "entity" to "CountEntry",
                            "entityId" to "${write.sessionId}:${write.departmentId}:${write.productId}",
                            "sessionId" to write.sessionId, "productId" to write.productId, "departmentId" to write.departmentId,
                            "actorUserId" to write.actorUserId, "actorName" to write.actorName,
                            "beforeVersion" to currentVersion, "afterVersion" to newVersion,
                            "quantity" to write.quantity, "note" to write.note, "source" to "web",
                            "createdAt" to ServerTime, "createdAtLocal" to write.createdAt
                        )), mergeOption())
                        if (write.resolutionOfConflictId != null && (existingConflict.exists as Boolean)) tx.delete(conflictRef)
                        WriteOutcome.Ack(newVersion)
                    }
                }
            }) })
        }).then({ outcome: dynamic ->
            writeInFlight = false
            if (sentIn != generation) return@then finishOldSessionWrite()
            retryDelayMs = 3000
            removeFromOutbox(write.eventId)
            when (val result = outcome.unsafeCast<WriteOutcome>()) {
                is WriteOutcome.Ack -> {
                    CountOutbox.rebase(loadOutbox(), write, result.version)?.let { saveOutbox(it) }
                    core?.applyCountAcknowledged(write, result.version)
                }
                is WriteOutcome.Conflict -> {
                    val fallback = CountConflict(
                        id = write.eventId, sessionId = write.sessionId, productId = write.productId, departmentId = write.departmentId,
                        productName = write.productId, localQuantity = write.quantity, localNote = write.note,
                        localActorId = write.actorUserId, localActorName = write.actorName, localBaseVersion = write.baseVersion,
                        remoteQuantity = result.remoteQuantity, remoteNote = result.remoteNote, remoteActorId = result.remoteActorId,
                        remoteVersion = result.remoteVersion, eventId = write.eventId, createdAt = nowMillis()
                    )
                    if (conflicts.none { it.id == fallback.id }) conflicts.add(0, fallback)
                    // Show the server's value (the user decides in the conflict list), not the refused one.
                    core?.applyConflictKeepCloud(write.sessionId, write.productId, write.departmentId,
                        result.remoteQuantity, result.remoteNote, result.remoteActorId, result.remoteVersion)
                }
            }
            state = CloudState.ONLINE
            changed()
            flushOutbox()
        }, { e: dynamic ->
            writeInFlight = false
            if (sentIn != generation) return@then finishOldSessionWrite()
            lastError = firebaseErrorMessage(e)
            val codeName = firebaseErrorCode(e).uppercase().replace('-', '_')
            if (CountOutbox.isPermanentFailure(codeName)) {
                // Retrying can never succeed (e.g. no access to this Hovedvare): drop it, show it.
                removeFromOutbox(write.eventId)
                rejected.add(0, RejectedCountWrite(write, lastError ?: t("Avvist av serveren", "Rejected by the server")))
                while (rejected.size > 50) rejected.removeAt(rejected.lastIndex)
                core?.applyCountNotPending(write, rejectedReason = lastError)
                lastRemoteCounts[countKey(write.sessionId, write.departmentId, write.productId)]?.let { core?.applyCloudCountEntries(listOf(it)) }
                state = CloudState.ERROR
                changed()
                flushOutbox()
            } else {
                // Offline or busy: stays in the outbox; retried when the network or a server snapshot is
                // back, and after a growing pause (3 s … 1 min) in case neither is noticed.
                state = if (codeName == "UNAVAILABLE" || window.navigator.onLine == false) CloudState.OFFLINE else CloudState.ERROR
                changed()
                val delay = retryDelayMs
                retryDelayMs = (retryDelayMs * 2).coerceAtMost(60_000)
                setTimeout(delay) { if (sentIn == generation && pendingWrites > 0) flushOutbox() }
            }
        })
    }

    private fun removeFromOutbox(eventId: String) = saveOutbox(loadOutbox().filterNot { it.eventId == eventId })

    private fun countKey(sessionId: String, departmentId: String, productId: String) = "$sessionId|$departmentId|$productId"

    /**
     * A count transaction finished after its session was signed out: the write stays in that
     * person's outbox (written events are recognised by id next time) and the delayed sign-out runs.
     */
    private fun finishOldSessionWrite() {
        if (signOutAfterWrite) {
            signOutAfterWrite = false
            if (membership == null && auth != null) auth.signOut()
        }
    }

    fun resolveConflictKeepCloud(conflict: CountConflict) {
        val m = membership ?: return
        core?.applyConflictKeepCloud(conflict.sessionId, conflict.productId, conflict.departmentId,
            conflict.remoteQuantity, conflict.remoteNote, conflict.remoteActorId, conflict.remoteVersion)
        conflicts.removeAll { it.id == conflict.id }
        remove("${WebPaths.conflicts(m.companyId, m.locationId, conflict.sessionId)}/${conflict.id}")
        changed()
    }

    fun resolveConflictKeepLocal(conflict: CountConflict) {
        val m = membership ?: return
        conflicts.removeAll { it.id == conflict.id }
        queueCountWrite(
            PendingCountWrite(
                eventId = randomId(), companyId = m.companyId, locationId = m.locationId, sessionId = conflict.sessionId,
                productId = conflict.productId, departmentId = conflict.departmentId, quantity = conflict.localQuantity,
                note = conflict.localNote, actorUserId = m.uid, actorName = m.displayName, baseVersion = conflict.remoteVersion,
                createdAt = nowMillis(), resolutionOfConflictId = conflict.id
            )
        )
    }

    // ------------------------------------------------------------------ people

    /**
     * Creates a Firebase account for a new person without signing the admin out (a second,
     * temporary Firebase app does the sign-up), then the membership in this company.
     */
    fun createCompanyUser(account: UserAccount, password: String, mustChangePassword: Boolean, onResult: (Result<String>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        val secondary: dynamic = sdk.initializeApp(FirebaseWebConfig.asJs(), "vareflow-provision-${randomId()}")
        activateAppCheck(secondary)
        val secondaryAuth = secondary.auth()
        fun cleanup() {
            runCatching { secondaryAuth.signOut() }
            runCatching { secondary.delete() }
        }
        secondaryAuth.createUserWithEmailAndPassword(account.username, password).then({ cred: dynamic ->
            val uid = cred.user.uid as String
            val payload = mapOf(
                "uid" to uid, "displayName" to account.name, "email" to account.username, "role" to account.role.name,
                "permissions" to account.permissions.map { it.name }, "active" to true, "theme" to "GLOD",
                "locationId" to m.locationId, "locationName" to m.locationName, "createdAt" to ServerTime
            ) + (if (mustChangePassword) mapOf("mustChangePassword" to true) else emptyMap()) +
                (account.accessUntil?.let { mapOf("accessUntil" to it) } ?: emptyMap())
            db.doc("${WebPaths.memberships(m.companyId)}/$uid").set(kotlinToJs(payload), mergeOption()).then({ _: dynamic ->
                cleanup()
                onResult(Result.success(uid))
            }, { e: dynamic ->
                // Remove the half-made account first, so the name can be used again.
                val failure = Result.failure<String>(IllegalStateException(friendlyAuthError(e)))
                cred.user.delete().then({ _: dynamic -> cleanup(); onResult(failure) }, { _: dynamic -> cleanup(); onResult(failure) })
            })
        }, { e: dynamic ->
            cleanup()
            onResult(Result.failure(IllegalStateException(friendlyAuthError(e))))
        })
    }

    /** Firebase's "choose your password" e-mail: the invitation for people with e-mail. */
    fun sendInviteEmail(email: String, onResult: (Result<Unit>) -> Unit) {
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        auth.languageCode = I18n.language.firebaseCode
        auth.sendPasswordResetEmail(email).then({ _: dynamic -> onResult(Result.success(Unit)) },
            { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    /** Mer → Bedrift (admin): name, short name and whether the name shows next to the logo. */
    fun updateCompanyProfile(name: String, shortName: String, headerDisplay: HeaderDisplay, onResult: (Result<Unit>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        val changes = mapOf("name" to name.trim(), "shortName" to shortName.trim(), "headerDisplay" to headerDisplay.name,
            "showNameWithLogo" to (headerDisplay != HeaderDisplay.LOGO))
        db.doc(WebPaths.company(m.companyId)).update(kotlinToJs(changes)).then({ _: dynamic ->
            // The login page shows the name from the Firmakode document.
            if (m.companyCode.isNotBlank()) {
                db.doc(WebPaths.companyCode(m.companyCode)).update(kotlinToJs(mapOf("name" to name.trim(), "shortName" to shortName.trim())))
                    .then({ _: dynamic -> }, { _: dynamic -> })
            }
            onResult(Result.success(Unit))
        }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    fun saveLogo(newLogo: CompanyLogo, onResult: (Result<Unit>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        CompanyLogo.problem(newLogo)?.let { return onResult(Result.failure(IllegalStateException(it))) }
        db.doc(WebPaths.logo(m.companyId)).set(kotlinToJs(CloudMapping.logoToCloud(newLogo, m.uid, nowMillis())))
            .then({ _: dynamic -> onResult(Result.success(Unit)) }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    fun removeLogo(onResult: (Result<Unit>) -> Unit) {
        val m = membership ?: return onResult(Result.failure(IllegalStateException(t("Logg inn på nytt.", "Log in again."))))
        db.doc(WebPaths.logo(m.companyId)).delete()
            .then({ _: dynamic -> onResult(Result.success(Unit)) }, { e: dynamic -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e)))) })
    }

    private fun currentUid(): String? {
        if (auth == null) return null
        val u = auth.currentUser
        return if (u == null || u == undefined) null else u.uid as String
    }

    /** The person's own language (`users/{uid}.language`), or null when none is saved or it cannot be read. */
    fun loadLanguage(onResult: (AppLanguage?) -> Unit) {
        val uid = currentUid()
        if (db == null || uid == null) return onResult(null)
        db.doc("users/$uid").get().then({ snap: dynamic ->
            val code = if (snap.exists as Boolean) snap.get("language") as String? else null
            onResult(AppLanguage.fromCode(code))
        }, { _: dynamic -> onResult(null) })
    }

    /** Saves the language on the person's account, so it follows them to other devices. */
    fun saveLanguage(language: AppLanguage) {
        val uid = currentUid()
        if (db == null || uid == null) return
        db.doc("users/$uid").set(kotlinToJs(mapOf("language" to language.code, "updatedAt" to ServerTime)), mergeOption())
            .then({ _: dynamic -> }, { _: dynamic -> })
    }

    /**
     * "Glemt passord?": Firebase's reset e-mail with a link to choose a new password.
     * An unknown address answers like a known one, so the form does not reveal who has an account.
     */
    fun sendPasswordReset(email: String, onResult: (Result<Unit>) -> Unit) {
        if (!ensure()) return onResult(Result.failure(IllegalStateException(lastError)))
        auth.languageCode = I18n.language.firebaseCode
        auth.sendPasswordResetEmail(email.trim()).then({ _: dynamic -> onResult(Result.success(Unit)) }, { e: dynamic ->
            when (firebaseErrorCode(e)) {
                "user-not-found" -> onResult(Result.success(Unit))
                "invalid-email", "missing-email" -> onResult(Result.failure(IllegalStateException(t("Skriv inn en gyldig e-postadresse.", "Enter a valid e-mail address."))))
                else -> onResult(Result.failure(IllegalStateException(friendlyAuthError(e))))
            }
        })
    }
}

/** Browser storage of one person's copy of one company (data cache and count outbox). */
fun cloudStorePrefix(m: WebMembership) = "vareflow.cloud.${m.companyId}.${m.uid}."

/** "Thorbjørnrud Hotell" → "thorbjornrud-hotell" (company / location ids). */
fun companySlug(name: String): String =
    name.trim().lowercase()
        .replace("æ", "ae").replace("ø", "o").replace("å", "a").replace("ä", "a").replace("ö", "o").replace("é", "e")
        .replace(Regex("[^a-z0-9]+"), "-").trim('-').take(40)

/** sessionStorage flag: signed in during this tab (kept across reloads, gone when the tab closes). */
private const val TAB_SESSION_KEY = "vareflow.firebase.tab_session"

private const val UPLOAD_DONE_VISIBLE_MS = 4000L
